import { borderRadius, colors } from '@/theme'
import CloseIcon from '@mui/icons-material/Close'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'

interface ChangeShippingTypeDialogProps {
  open: boolean
  currentShippingType: string
  nextShippingType: string
  isLoading: boolean
  onConfirm: () => void
  onClose: () => void
}

/**
 * Confirms a shipping type change on an order that already has a shipping label. Saving
 * regenerates the shipment, so the current label stops being valid — CS needs to know that
 * before it happens, not after.
 */
export default function ChangeShippingTypeDialog({
  open,
  currentShippingType,
  nextShippingType,
  isLoading,
  onConfirm,
  onClose
}: ChangeShippingTypeDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: borderRadius.md } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          pb: 1
        }}
      >
        <Typography variant='sectionTitle'>Change shipping type</Typography>
        <IconButton onClick={onClose} size='small' sx={{ mt: -0.5 }} disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: 14, color: colors.text.primary, mb: 1.5 }}>
          Change from <strong>{currentShippingType || 'the current shipping type'}</strong> to{' '}
          <strong>{nextShippingType}</strong>?
        </Typography>
        <Typography sx={{ fontSize: 14, color: colors.text.secondary, mb: 1.5 }}>
          This order already has a shipping label. Saving will cancel the current Royal Mail shipment and create a new
          label on the new shipping type. The label and shipping tag held by the pharmacy in Titan are updated
          automatically.
        </Typography>
        <Typography sx={{ fontSize: 14, color: colors.text.secondary }}>
          The customer is not charged for the change. If the parcel has already been packed with the old label, let the
          pharmacy know.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant='ghost' onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant='primary'
          startIcon={<LocalShippingOutlinedIcon />}
          onClick={onConfirm}
          disabled={isLoading}
          loading={isLoading}
        >
          Change and update label
        </Button>
      </DialogActions>
    </Dialog>
  )
}
